import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { guidelineView, openRecord, whenIsBareSource } from "./sourceLinks";
import { InfoButton, InfoPopover, useInfo } from "./RecInfo";
import { infoDetail, infoProvenance, rowText, splitLastWord, titleParts } from "./recText";
import {
  colourLevel, groupByLane, laneCounts, laneCountsLabel, laneName, laneOf, laneOrder, leadArrow, leadEvidence, leadSr, nameBeforeStatus,
  recKind, recLead, trimLead,
} from "./recLanes";
import { NEXT_PLACE, NEXT_TAB, VERB_COL_MAX, actionsCell, bandFirst, ledgerCount, orderDisplay, pointerOf } from "./orderLedger";
import { chartTabAllowed } from "../../../../../config/practice";
import { useOrdersShown } from "./ordersShown";
import OrderDx, { orderDxOf } from "./OrderDx";

/*
 * AI Recommendations (CONTRACT §3.2 / §4.1-4.2; owner 10-06: variant B1 "quiet
 * lanes", then "a bit more colour - to draw the eye"; recs-redesign/lanes; owner
 * 10-07: V4 "order ledger", without the review column, actions-views):
 *   drawer  before recording: three lanes, Act now / Due / Consider (critical +
 *           overdue / due / consider + risk), each a label with its open count
 *           and a rule down its rows, under one Finding | Actions header. A row
 *           is the value lead (the record's own value + arrow, its date grey:
 *           recLanes.js, never the model's text; a button that opens that
 *           record), the finding (the headline's first half, careiq.n3.3, or the
 *           sentence clipped at a clause; recText.js) with a tiny (i) glued to
 *           its last word that holds the full sentence, the records, the
 *           guideline link, why there is no order and the provenance
 *           (RecInfo.jsx), then the Actions cell (orderLedger.js): the order as
 *           ONE quiet button, a grey verb and the order's short name in teal with
 *           its ⊕ at the cell's right edge (every ⊕ in one line), "✓ Added" + Undo
 *           in place once added; a shared order's pointer to the row that adds
 *           it; a grey link with › where the work happens; or the action in plain
 *           grey. No filled button and no review control anywhere (owner 10-07:
 *           "the 'review' column/button seems pointless"); adding an order is
 *           what handles a row (it stays where it is, quiet, until the list is
 *           reopened; "Reviewed today (N) · Undo" lists the ones handled before).
 *           The header counts the orders ("6 orders suggested", "1 of 6 orders
 *           added"). Then the "Merged from ..." line and the quiet "Show orders
 *           without recording".
 *           The row lays out by the drawer's OWN width (CSS container queries,
 *           n3recs on .n3-band-wrap; never the screen's): one line from 870 px
 *           of drawer (the verb in a column of its own so the order names line
 *           up like an order sheet), lead | finding | Actions from 620 px, the
 *           Actions cell under the finding below that, the lead on a line of its
 *           own below 530 px. A list with nothing for the Actions column (no
 *           order, no action text: fallback sentences) has no header or column.
 *   band    recording and after: the top item flat in its lane (the same row,
 *           no header) + the hidden items counted by lane ("5 more ●1 ■3 ○1 Show
 *           all", spoken as "Show all, 5 more: 1 act now, 3 due, 1 consider").
 *           The top item is the first open row (lane order) whose order is still
 *           to place, else the first not yet placed, else the first
 *           (orderLedger.js bandFirst): with no review control nothing else
 *           moves the band on, so a link, a plain action or an order already on
 *           the visit never holds it for the whole recording.
 *   line    everything handled: one grey line.
 * How much colour: data-colour on the section (1 quiet / 2 accent / 3 vivid,
 * recLanes.js COLOUR_LEVEL; the look lives in CareIQTabN3.css).
 * Drawer -> band is a 200 ms height morph whose content dips to 35 % and
 * back, never through blank; instant with prefers-reduced-motion.
 */

const reducedMotion = () => {
  try {
    return !window.matchMedia || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_) {
    return true;
  }
};

export const Chevron = ({ up }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d={up ? "m18 15-6-6-6 6" : "m6 9 6 6 6-6"} />
  </svg>
);
// the order's ⊕: a ring and a plus (the ring fills on hover); drawn, so every ⊕ is the same size
const PlusRing = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle className="n3-oc-ring" cx="12" cy="12" r="9.6" />
    <path className="n3-oc-pl" d="M12 7.6v8.8M7.6 12h8.8" />
  </svg>
);
const GoIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 3.5 10.5 8 6 12.5" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.2 8.4l3 3 6.6-7" />
  </svg>
);
const OrdersIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
    <rect x="3" y="2.5" width="10" height="11.5" rx="1.6" />
    <path d="M5.6 6h4.8M5.6 8.6h4.8M5.6 11.2h2.6" />
  </svg>
);
// A value change's arrow ("eGFR 61 → 52", "(Mar→Aug)": the only arrow a title keeps; titleParts), drawn: the
// web font's Latin subset has ↑ / ↓ but no → (iPad Safari would take a fallback font's glyph). Screen
// readers and copy still get the glyph as written.
const TrendArrow = ({ glyph = "→" }) => (
  <span className="n3-ar">
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 8h10.5M9 4l4 4-4 4" />
    </svg>
    <span className="n3-sr">{glyph}</span>
  </span>
);

// The kind cue (shown at colour level 3 only), 14 px line icons.
const KIND_PATHS = {
  lab: ["M9 3h6", "M10 3v6.2L5.2 17.6A2.2 2.2 0 0 0 7.1 21h9.8a2.2 2.2 0 0 0 1.9-3.4L14 9.2V3", "M7.6 14h8.8"],
  screening: ["M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13z", "M20 20l-4.8-4.8", "M8 10.6l1.8 1.8 3.4-3.6"],
  med: ["M7 3h10v4H7z", "M6 7h12v12.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 19.5z", "M12 11v6", "M9 14h6"],
  risk: ["M3 17l6-6 4 4 8-8", "M14 7h7v7"],
  vaccine: ["M12 3l7.5 3v5.5c0 4.4-3.1 8.1-7.5 9.5-4.4-1.4-7.5-5.1-7.5-9.5V6z", "M12 9v6", "M9 12h6"],
  other: ["M9 4h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z", "M16 5h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2"],
};
const KindIcon = ({ kind }) => (
  <span className={`n3-kind k-${kind}`} aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      {(KIND_PATHS[kind] || KIND_PATHS.other).map((d) => <path key={d} d={d} />)}
    </svg>
  </span>
);

// The value lead: "UACR 48 ↑ · Aug 2025" (the record's own value), "CT abdomen/pelvis · Aug 22"
// (a record without a value), or nothing. Colour (by level) is never the only cue: the arrow / word
// is drawn and the screen reader hears "high" / "critical high".
// A long lead puts its date on a line of its own; a name too long for the lead column wraps onto
// lines of its own (never clipped), and the value + its arrow (.n3-vl-q) never split.
export const LEAD_ONE_LINE = 15;
// What the lead button says (owner 10-07: "maybe we make the reading clickable?"): "Open Hemoglobin A1c
// 7.9 % high from May 30", "Open Retinal Diagnostic Report from Mar 2024".
export const leadOpenLabel = (lead) => {
  const what = [lead.full || lead.name, lead.kind === "value" ? `${lead.value}${lead.units ? ` ${lead.units}` : ""}` : "", leadSr(lead)].filter(Boolean).join(" ");
  return `Open ${what}${lead.date ? ` from ${lead.date}` : ""}`.replace(/\s+/g, " ").trim();
};
// A lead with a record behind it (the evidence it was read from, opened as the (i)'s record link opens
// it: sourceLinks.openRecord) is a button that opens that record: the lab popup of that result date, or
// the document / imaging viewer. No record: plain text, never a fake link.
export function Lead({ lead, kind, record = null, onOpen = null }) {
  const long = Boolean(lead && lead.date && `${lead.name}${lead.value || ""}${lead.date}`.length > LEAD_ONE_LINE);
  const body = (
    <>
      <KindIcon kind={kind} />
      {lead ? <span className="n3-vl-n">{lead.name}</span> : null}
      {lead && lead.kind === "value" ? (
        <span className="n3-vl-q">
          <span className={`n3-vl-v${lead.sev ? ` sev-${lead.sev}` : ""}`}>{lead.value}</span>
          {lead.dir ? (
            <>
              <span className={`n3-vl-a sev-${lead.sev}${/[a-z]/.test(leadArrow(lead)) ? " w" : ""}`} aria-hidden="true">{leadArrow(lead)}</span>
              <span className="n3-sr"> {leadSr(lead)}</span>
            </>
          ) : null}
        </span>
      ) : null}
      {lead && lead.date ? (
        <span className="n3-vl-d">
          {lead.name ? <span className="n3-vl-sep" aria-hidden="true">·</span> : null}
          {lead.name ? <span className="n3-sr">, </span> : null}
          {lead.date}
        </span>
      ) : null}
    </>
  );
  const cls = `n3-vl${long ? " wrap" : ""}`;
  if (lead && record && onOpen) {
    const tip = record.kind === "lab" ? "Open lab result" : "Open document";
    return (
      <button
        type="button"
        className={`${cls} n3-vl-btn no-hover-tint`}
        data-lead={lead.kind}
        data-act="lead"
        data-ev={record.kind}
        aria-label={leadOpenLabel(lead)}
        title={lead.tip ? `${tip}\n${lead.tip}` : tip}
        onClick={(e) => { e.stopPropagation(); onOpen(record); }}
      >
        {body}
      </button>
    );
  }
  return (
    <span className={cls} data-lead={lead ? lead.kind : "none"} title={lead ? lead.tip : undefined}>
      {body}
    </span>
  );
}

function Part({ p }) {
  if (p.k === "date") return <span className="n3-hl-d">{p.t}</span>;
  if (p.k === "arrow") return <TrendArrow glyph={p.t} />;
  return p.t;
}

// Text whose last word stays on one line with what follows it (the (i)).
function Glued({ text, after }) {
  const [head, tail] = splitLastWord(titleParts(text));
  return (
    <>
      {head.map((p, i) => <Part key={i} p={p} />)}
      <span className="n3-nw">
        {tail ? <Part p={tail} /> : null}
        {after}
      </span>
    </>
  );
}

// The title as two halves, never an arrow (owner 10-07: arrows mean a lab going up / down): what it
// finds (ink, regular), then what to do (.n3-hl-act: semibold, capitalised), on the same line after a
// gap when it fits, else on a line of its own (the "Reviewed today" list; a lane row's what-to-do is its
// Actions cell). No action: the finding alone. The (i) follows the last half; with neither (a finding
// that was all lead) it stands alone.
export function RecTitle({ finding, action, after = null }) {
  return (
    <span className={`n3-hl${action ? " has-act" : ""}`}>
      {finding ? (
        <span className="n3-hl-f">
          {action ? titleParts(finding).map((p, i) => <Part key={i} p={p} />) : <Glued text={finding} after={after} />}
        </span>
      ) : null}
      {finding && action ? <span className="n3-sr">: </span> : null}
      {action ? <span className="n3-hl-act"><Glued text={action} after={after} /></span> : null}
      {!finding && !action && after ? <span className="n3-hl-f n3-hl-i">{after}</span> : null}
    </span>
  );
}

// The cell's two lines: the grey verb (an empty one keeps the wide row's verb column, so the names line
// up) and the name. A verb longer than the verb column takes a line of its own (.long).
function CellText({ verb = "", verbNode = null, children, long = false }) {
  return (
    <span className={`n3-oc-t${long || verb.length > VERB_COL_MAX ? " long" : ""}`}>
      <span className="n3-oc-v">{verbNode || verb}</span>
      <span className="n3-oc-n">{children}</span>
    </span>
  );
}

// The Actions cell (orderLedger.js actionsCell): see the head of this file.
export function OrderCell({ cell, rec, info, done = false, stateOk = false, pointer = null, sharedInfo = null, onAdd, onUndoAdd, onUndo, onGoTo, onNext, after = null }) {
  const addRef = useRef(null);
  const undoRef = useRef(null);
  const want = useRef(null);
  // focus follows the action: Undo after an add, the order again after an undo (the button it was on is gone)
  useEffect(() => {
    if (want.current === "undo" && undoRef.current) { undoRef.current.focus({ preventScroll: true }); want.current = null; }
    else if (want.current === "add" && addRef.current) { addRef.current.focus({ preventScroll: true }); want.current = null; }
    else if (info && info.error) want.current = null;
  });
  const viaOrder = Boolean(done && rec.reviewed && rec.reviewed.via === "order");
  const busy = Boolean(info && info.busy);
  // the practice places no orders from CareIQ (config/practice.js, 7825), or this view is not today's
  // live visit (N3/ordersShown.js): an order to place reads as text
  const ordersOn = useOrdersShown();
  if (cell.kind === "order" && (done || (info && info.state === "added"))) {
    const canUndo = done ? stateOk : Boolean(ordersOn && info && info.canUndo);
    const word = done && !viaOrder ? "Reviewed" : info && info.short ? info.short : "Added";
    return (
      <div className={`n3-oc n3-oc-added${done && !viaOrder ? " rv" : ""}`} data-oc="added" title={info && info.text ? info.text.replace(/^✓\s*/, "") : undefined}>
        <CellText verbNode={<><CheckIcon />{word}</>}>{cell.name}</CellText>
        {canUndo ? (
          <button
            type="button"
            ref={undoRef}
            className="n3-oc-undo no-hover-tint"
            data-act="undo-add"
            aria-label={`Undo: remove ${cell.name} from this visit’s orders`}
            onClick={() => { want.current = "add"; if (done) onUndo(rec); else onUndoAdd(rec); }}
          >
            Undo
          </button>
        ) : null}
      </div>
    );
  }
  if (cell.kind === "order" && !ordersOn) {
    return (
      <div className="n3-oc n3-oc-plain" data-oc="order-text">
        <CellText verb={cell.verb}>{cell.name}</CellText>
      </div>
    );
  }
  if (cell.kind === "order") {
    const err = info && info.error;
    const ordername = (rec.action && rec.action.ordername) || cell.name;
    return (
      <button
        type="button"
        ref={addRef}
        className={`n3-oc n3-oc-add no-hover-tint${err ? " err" : ""}`}
        data-act="add"
        disabled={busy}
        aria-busy={busy || undefined}
        aria-label={`${err ? "Not added. Retry: add" : cell.verb ? `${cell.verb}: add` : "Add"} ${cell.name} to this visit’s orders`}
        title={`Adds ${ordername} to this visit’s orders`}
        onClick={() => { want.current = "undo"; onAdd(rec); }}
      >
        <CellText verb={err ? "" : cell.verb} verbNode={err ? <span className="n3-oc-err">{err}</span> : null}>{cell.name}</CellText>
        <span className="n3-oc-i"><PlusRing /></span>
      </button>
    );
  }
  if (cell.kind === "shared") {
    const added = viaOrder || Boolean(sharedInfo && sharedInfo.state === "added") || Boolean(info && info.state === "added");
    // the carrier's word: "Added" (this tab added it), "Ordered" (it was already on the visit)
    const word = !viaOrder && sharedInfo && sharedInfo.state === "added" && sharedInfo.short ? sharedInfo.short : "Added";
    const name = cell.name || (pointer && pointer.target ? orderDisplay(pointer.target.action) : "");
    const label = pointer && pointer.label;
    return (
      <div className={`n3-oc n3-oc-shared${added ? " is-added" : ""}`} data-oc="shared">
        {/* its verb over its name at every width: the pointer takes the room beside the name (.long) */}
        <CellText long verb={added ? "" : cell.verb} verbNode={added ? <><CheckIcon />{word}</> : null}>{name}</CellText>
        {label ? (
          <button
            type="button"
            className="n3-oc-ptr no-hover-tint"
            data-act="goto"
            data-to={pointer.to}
            aria-label={`${name || "This order"} is added on the ${label} row: go to it`}
            title={`${name || "This order"} is added on the ${label} row (one order covers both)`}
            onClick={() => onGoTo && onGoTo(pointer.to)}
          >
            <span aria-hidden="true">{pointer.dir || "↑"} </span>{label}
          </button>
        ) : null}
      </div>
    );
  }
  // a link to a chart tab the practice does not have (7825: Labs, Documents, RPM, Prior visits) reads as text
  if (cell.kind === "link" && !chartTabAllowed(NEXT_TAB[cell.target])) {
    return (
      <div className="n3-oc n3-oc-plain" data-oc="link-text">
        <CellText verb={cell.verb}>{cell.label}</CellText>
      </div>
    );
  }
  if (cell.kind === "link") {
    const place = NEXT_PLACE[cell.target] || cell.label;
    return (
      <button
        type="button"
        className="n3-oc n3-oc-go no-hover-tint"
        data-act="next"
        data-target={cell.target}
        aria-label={`${cell.verb ? `${cell.verb}: ` : ""}${cell.label}, opens ${place}`}
        title={`Opens ${place}`}
        onClick={() => onNext && onNext(rec.next_step)}
      >
        <CellText verb={cell.verb}>{cell.label}</CellText>
        <span className="n3-oc-i go"><GoIcon /></span>
      </button>
    );
  }
  // plain: nothing to place; the (i) (why) follows the text when the finding is empty
  return (
    <div className="n3-oc n3-oc-plain" data-oc="plain">
      <CellText long>{after ? <Glued text={cell.text} after={after} /> : cell.text}</CellText>
    </div>
  );
}

// What the (i) adds for the Actions cell: the order it places (its catalogue name and type), or why there
// is none (careiq.n3.4 no_order_reason, on a plain action or a link).
const orderNote = (rec, cell) => {
  if (!cell) return null;
  if ((cell.kind === "order" || cell.kind === "shared") && rec.action) {
    return { k: "Order", v: rec.action.ordername || cell.name, t: rec.action.ordertype || "" };
  }
  // no order: why (careiq.n3.4 no_order_reason), for a plain action and for a link alike ("A device problem,
  // not a clinical order" behind the RPM row's "RPM readings ›")
  if ((cell.kind === "plain" || cell.kind === "link") && cell.reason) return { k: "No order", v: cell.reason, t: "" };
  return null;
};

// The name a shared order's pointer gives the row that adds it: its lead ("eGFR"), else the test's name
// before a status word in its finding ("ALT stale since ..." -> "ALT": a row without a record behind it,
// careiq.n3.4 real bodies), else its finding clipped.
const rowLabel = (r, leadOpts) => {
  const lead = recLead(r, leadOpts);
  if (lead && lead.name) return lead.name;
  const f = rowText(r, (t) => trimLead(t, lead)).finding;
  const name = nameBeforeStatus(f);
  if (name) return name;
  return f.length > 24 ? `${f.slice(0, 22).replace(/\s+\S*$/, "")}…` : f;
};

// One recommendation as a lane row. `band`: the collapsed band's one item. `cols`: the list has an
// Actions column (the row's cell, or an empty one).
export function RecRow({ rec, top = false, band = false, cols = false, flash, justDone, info, infoOf, stateOk, onAdd, onUndoAdd, onUndo, onGoTo, onNext, ordered, refYear, critLabs, onOpenEvidence, dxPick = null, onPickDx = null }) {
  const pop = useInfo();
  const ordersOnRow = useOrdersShown();
  const lane = laneOf(rec.priority);
  const kind = recKind(rec);
  const leadOpts = { refYear, critLabs };
  const lead = recLead(rec, leadOpts);
  // the record the lead was read from, as the (i)'s record link opens it (null: the lead is plain text)
  const leadRecord = lead ? openRecord(leadEvidence(rec, leadOpts), refYear) : null;
  const leadEl = <Lead lead={lead} kind={kind} record={leadRecord} onOpen={onOpenEvidence} />;
  // a served headline that restates the lead loses the repeated part (text is only ever removed), then
  // splits into what it finds and what to do (no arrow on the row); a fallback sentence stays whole
  const hl = rowText(rec, (t) => trimLead(t, lead));
  const cell = cols ? actionsCell(rec, hl.action) : null;
  const open = rec.status === "open";
  const pointer = cell && cell.kind === "shared" ? pointerOf(rec, ordered, (t) => rowLabel(t, leadOpts)) : null;
  const sharedInfo = pointer && pointer.target && infoOf ? infoOf(pointer.target) : null;
  // the (i) explains the finding: glued to its last word; no finding (it was all lead) and nothing to place,
  // it follows the plain action; else it stands alone in the Finding cell. A handled row has none.
  const infoBtn = open ? <InfoButton info={pop} /> : null;
  const iInCell = Boolean(cell && cell.kind === "plain" && !hl.finding);
  const colCls = cols ? ` n3-colrow${cell ? ` oc-${cell.kind}` : " no-cell"}${hl.finding ? "" : " no-find"}` : "";
  const note = orderNote(rec, cell);
  const bare = whenIsBareSource(rec);
  // the order's diagnosis (careiq.n3.4 action.orderDx, OrderDx.jsx): under an order still to place, beside
  // (never inside) its button; the chosen code goes with the order when its ⊕ adds it
  const showDx = Boolean(cell && cell.kind === "order" && open && !(info && info.state === "added") && ordersOnRow && orderDxOf(rec));
  return (
    <div
      className={`n3-brow n3-lrow${open ? "" : " done"}${colCls}${band ? " n3-band-item" : ""}${top && open ? " top" : ""}${flash ? " flash" : ""}${justDone && (band || !open) ? " n3-slide" : ""}${lead ? "" : " no-lead"}`}
      role="listitem"
      data-rec={open ? rec.issue_key : undefined}
      data-done={open ? undefined : rec.issue_key}
      data-priority={rec.priority}
      data-lane={lane}
      data-kind={kind}
    >
      {leadEl}
      <div className="n3-brow-txt" data-title={hl.title.source} data-trimmed={hl.trimmed ? "1" : undefined}>
        <RecTitle finding={hl.finding} action={cols ? "" : hl.action} after={iInCell ? null : infoBtn} />
      </div>
      {cell ? (
        <div className="n3-brow-act" data-cell={cell.kind}>
          <span className="n3-sr">Action: </span>
          <OrderCell
            cell={cell}
            rec={rec}
            info={info}
            done={!open}
            stateOk={stateOk}
            pointer={pointer}
            sharedInfo={sharedInfo}
            onAdd={onAdd}
            onUndoAdd={onUndoAdd}
            onUndo={onUndo}
            onGoTo={onGoTo}
            onNext={onNext}
            after={iInCell ? infoBtn : null}
          />
          {showDx ? <OrderDx rec={rec} picked={dxPick} onPick={onPickDx} /> : null}
        </div>
      ) : null}
      {open ? (
        <InfoPopover
          info={pop}
          rec={rec}
          title={hl.plain}
          detail={infoDetail(rec)}
          order={note}
          prov={infoProvenance(rec, { whenIsBare: bare, hasGuideline: Boolean(guidelineView(rec)) })}
          refYear={refYear}
          onOpenEvidence={onOpenEvidence}
        />
      ) : null}
    </div>
  );
}

// "Reviewed today (N) ▾ · Undo" (items handled at this visit, by an order added here or by an earlier
// review, that are not in the list above), with the expandable list.
export function RevGroup({ items, open, onToggle, onUndoLast, onUndo, noteFor }) {
  if (!items.length) return null;
  return (
    <div className="n3-band-rev">
      <button type="button" className="no-hover-tint" aria-expanded={open} onClick={onToggle}>
        Reviewed today ({items.length}) {open ? "▴" : "▾"}
      </button>
      <span>·</span>
      <button type="button" className="no-hover-tint" onClick={onUndoLast}>Undo</button>
      {open && (
        <div className="n3-rev-list">
          {items.map((r) => {
            const h = rowText(r);
            return (
              <div className="n3-rrow" key={r.issue_key}>
                <div className="n3-rrow-t">
                  <RecTitle finding={h.finding} action={h.action} />
                  <span className="n3-rrow-o">{noteFor(r)}</span>
                </div>
                <button type="button" className="n3-btn-text no-hover-tint" onClick={() => onUndo(r)}>Undo</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// A lane's head: "ACT NOW 2" (its open items; none left open: no number).
function LaneHead({ id, n }) {
  return (
    <div className="n3-lane-h">
      <span className="n3-lane-lbl">
        <span className="n3-lane-dot" aria-hidden="true" />
        {laneName(id)}
        {n ? <span className="n3-lane-n">{n}</span> : null}
      </span>
    </div>
  );
}

const rowProps = (r, ctx) => ({
  rec: r,
  flash: ctx.flashKey === r.issue_key,
  justDone: ctx.justDone === r.issue_key,
  info: ctx.infoFor(r),
  infoOf: ctx.infoFor,
  stateOk: ctx.stateOk,
  onAdd: ctx.onAdd,
  onUndoAdd: ctx.onUndoAdd,
  onUndo: ctx.onUndo,
  onGoTo: ctx.onGoTo,
  onNext: ctx.onNext,
  ordered: ctx.allOrdered,
  refYear: ctx.refYear,
  critLabs: ctx.critLabs,
  onOpenEvidence: ctx.onOpenEvidence,
  dxPick: ctx.dxPickFor ? ctx.dxPickFor(r) : null,
  onPickDx: ctx.onPickDx || null,
});

// Does this row have anything for the Actions column (an order, a pointer, a link, an action text)?
const hasCell = (r, leadOpts) => Boolean(actionsCell(r, rowText(r, (t) => trimLead(t, recLead(r, leadOpts))).action));

// The header's order count: what the list would place, and how much of it is placed (no filled button).
export function OrderTally({ items, ctx }) {
  const isAdded = (r) => {
    const i = ctx.infoFor(r);
    return Boolean(i && i.state === "added") || Boolean(r.status === "reviewed" && r.reviewed && r.reviewed.via === "order");
  };
  const ordersOn = useOrdersShown();
  const { total, added } = ledgerCount(items, isAdded);
  // no order count where nothing can be added here (7825; a view that is not today's live visit)
  if (!total || !ordersOn) return null;
  const one = total === 1;
  return added ? (
    <span className="n3-tally has-added" data-tally={`${added}/${total}`}>
      <CheckIcon />
      <span><b className="n">{added}</b> of {total} <span className="n3-tally-long">{one ? "order " : "orders "}</span>added</span>
    </span>
  ) : (
    <span className="n3-tally" data-tally={`0/${total}`}>
      <OrdersIcon />
      <span><b>{total}</b> {one ? "order" : "orders"}<span className="n3-tally-long"> suggested</span></span>
    </span>
  );
}

// The lanes of the drawer / overlay: the snapshot taken when the list opened,
// so a handled item confirms where it is instead of jumping away. Every row is its own grid whose tracks
// are fixed, or flexible with a fixed floor, the same in every row (CareIQTabN3.css "the Finding | Actions
// columns"), so the Finding / Actions columns and the ⊕ line up across rows and lanes with nothing measured.
export function RecList({ view, byKey, total, ctx }) {
  const items = view.map((k) => byKey.get(k)).filter(Boolean);
  const groups = groupByLane(items);
  const ordered = groups.flatMap((g) => g.items);
  const firstOpen = ordered.find((r) => r.status === "open");
  // no item with a record to lead with (the legacy fallback, an n3.1 server): no empty lead column
  const leadOpts = { refYear: ctx.refYear, critLabs: ctx.critLabs };
  const noLeads = !ordered.some((r) => recLead(r, leadOpts));
  // nothing for the Actions column anywhere (no order, no action text: an n3.1 / n3.2 / legacy list of
  // fallback sentences without orders): no Finding | Actions header and no Actions column
  const noActs = !ordered.some((r) => hasCell(r, leadOpts));
  if (!groups.length) {
    return (
      <div className="n3-band-list n3-lanes">
        <div className="n3-brow n3-empty">
          <div className="n3-brow-prov">{ctx.emptyText || `Nothing open — all ${total} reviewed today.`}</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`n3-band-list n3-lanes n3-cols${noLeads ? " no-leads" : ""}${noActs ? " no-acts" : ""}`}>
      {noActs ? null : (
        <div className="n3-cols-h" aria-hidden="true">
          <span className="n3-cols-t">Finding</span>
          <span className="n3-cols-a">Actions</span>
        </div>
      )}
      {groups.map((g) => (
        <div key={g.lane.id} className={`n3-lane lane-${g.lane.id}`} role="group" aria-label={g.lane.name} data-lane={g.lane.id}>
          <LaneHead id={g.lane.id} n={g.items.filter((r) => r.status === "open").length} />
          <div role="list">
            {g.items.map((r) => (
              <RecRow
                key={r.issue_key}
                {...rowProps(r, ctx)}
                cols={!noActs}
                top={Boolean(firstOpen && firstOpen.issue_key === r.issue_key)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// "●1 ■3 ○1": the items behind "Show all", counted by lane (circle / square / ring, so the
// lanes differ by shape as well as colour; the button's name says it in words).
function LaneCounts({ list }) {
  const counts = laneCounts(list);
  if (!counts.length) return null;
  return (
    <span className="n3-lcnt" aria-hidden="true">
      {counts.map((c) => (
        <span key={c.lane} className={`n3-lcnt-i c-${c.lane}`}><i />{c.n}</span>
      ))}
    </span>
  );
}

function DrawerBody({ mode, view, byKey, open, total, ctx, merged, ovlOpen, onShowAll, onEscape, escapeRef, lastTopRef }) {
  if (mode === "line") {
    return (
      <>
        <span>AI Recommendations · all {total} reviewed today</span>
        <span>·</span>
        <button type="button" className="no-hover-tint" data-act="showall" aria-expanded={ovlOpen} aria-controls="n3-ovl" onClick={onShowAll}>Show</button>
      </>
    );
  }
  if (mode === "band") {
    // the first open row with an order still to place leads (orderLedger.js bandFirst: no review control)
    const ordered = bandFirst(laneOrder(open), ctx.infoFor, byKey);
    const t = ordered[0];
    const rest = ordered.slice(1);
    const lane = t ? laneOf(t.priority) : null;
    const slide = lastTopRef && lastTopRef.current && t && lastTopRef.current !== t.issue_key;
    return (
      <>
        <div className="n3-band-h">
          <span className="n3-band-lbl">AI Recommendations</span>
          {t ? (
            <span className={`n3-band-lane lane-${lane}`}>
              <LaneHead id={lane} n={0} />
            </span>
          ) : null}
          <button
            type="button"
            className="n3-band-more no-hover-tint"
            data-act="showall"
            aria-expanded={ovlOpen}
            aria-controls="n3-ovl"
            aria-label={laneCountsLabel(rest)}
            title={rest.length ? laneCountsLabel(rest) : undefined}
            onClick={onShowAll}
          >
            {rest.length ? <span className="n3-more-n">{rest.length} more</span> : <span>Nothing else open</span>}
            <LaneCounts list={rest} />
            <span className="n3-more-s">Show all</span>
            <Chevron />
          </button>
        </div>
        {t && (
          <div className={`n3-lane lane-${lane} is-band${recLead(t, { refYear: ctx.refYear, critLabs: ctx.critLabs }) ? "" : " no-leads"}`} role="group" aria-label={laneName(lane)} data-lane={lane}>
            <div role="list">
              <RecRow
                key={t.issue_key}
                {...rowProps(t, ctx)}
                band
                top
                cols={hasCell(t, { refYear: ctx.refYear, critLabs: ctx.critLabs })}
                justDone={Boolean(slide)}
              />
            </div>
          </div>
        )}
      </>
    );
  }
  const nOpen = view.filter((k) => byKey.get(k) && byKey.get(k).status === "open").length;
  return (
    <>
      <div className="n3-band-h">
        <span className="n3-band-lbl">AI Recommendations</span>
        <span className="n3-band-sub">{nOpen ? `${nOpen} open` : "nothing open"}</span>
        <OrderTally items={view.map((k) => byKey.get(k)).filter(Boolean)} ctx={ctx} />
      </div>
      <RecList view={view} byKey={byKey} total={total} ctx={ctx} />
      {ctx.stateOk && (
        <RevGroup items={ctx.revItems(view)} open={ctx.revOpen} onToggle={ctx.onToggleRev} onUndoLast={ctx.onUndoLast} onUndo={ctx.onUndo} noteFor={ctx.noteFor} />
      )}
      <div className="n3-band-foot">
        <span className="n3-band-merged" title={merged || undefined}>{merged}</span>
        {/* (not where the tab has no order zone: onEscape is left out, 7825) */}
        {onEscape ? (
          <button
            type="button"
            ref={escapeRef}
            className="n3-esc-link no-hover-tint"
            data-act="orders"
            title="For visits you will not record: nurse visit, walk-in, or to order first"
            onClick={onEscape}
          >
            Show orders without recording
          </button>
        ) : null}
      </div>
      {ctx.stateNote ? <div className="n3-band-note">{ctx.stateNote}</div> : null}
    </>
  );
}

const bandClass = (mode) => (mode === "line" ? "n3-band is-line" : mode === "band" ? "n3-band is-collapsed" : "n3-band is-drawer");

export default function RecsDrawer(props) {
  const { mode } = props;
  const bandRef = useRef(null);
  const measureRef = useRef(null);
  const lastTopRef = useRef(null);
  const grow = useRef(null);
  const fadeIn = useRef(false);
  const [shown, setShown] = useState(mode);
  const [measuring, setMeasuring] = useState(null);

  // 1. a new mode: measure it in a hidden copy (or switch at once).
  useLayoutEffect(() => {
    if (mode === shown && !measuring) return;
    const el = bandRef.current;
    if (!el || reducedMotion() || typeof el.animate !== "function") {
      setMeasuring(null);
      setShown(mode);
      return;
    }
    if (measuring !== mode) setMeasuring(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // 2. measured: grow = new content then open up; shrink = old content dims
  //    while the box closes, then the new content fades back from 35 %.
  useLayoutEffect(() => {
    if (!measuring) return;
    const target = measuring;
    const el = bandRef.current;
    const h0 = el ? el.offsetHeight : 0;
    const h1 = measureRef.current ? measureRef.current.offsetHeight : h0;
    setMeasuring(null);
    if (!el || Math.abs(h1 - h0) < 2) { setShown(target); return; }
    if (h1 > h0) { grow.current = { h0, h1 }; setShown(target); return; }
    el.style.overflow = "hidden";
    const kids = [...el.children];
    const dims = kids.map((k) => k.animate([{ opacity: 1 }, { opacity: 0.35 }], { duration: 200, easing: "linear", fill: "forwards" }));
    const a = el.animate([{ height: `${h0}px` }, { height: `${h1}px` }], { duration: 200, easing: "cubic-bezier(.4,0,.2,1)" });
    const done = () => {
      el.style.overflow = "";
      dims.forEach((d) => { try { d.cancel(); } catch (_) {} });
      fadeIn.current = true;
      setShown(target);
    };
    a.finished.then(done, done);
  }, [measuring]);

  // 3. after a switch: finish the grow / fade the new content in.
  useLayoutEffect(() => {
    const el = bandRef.current;
    if (!el) return;
    if (grow.current) {
      const { h0, h1 } = grow.current;
      grow.current = null;
      el.style.overflow = "hidden";
      const a = el.animate([{ height: `${h0}px` }, { height: `${h1}px` }], { duration: 200, easing: "cubic-bezier(.4,0,.2,1)" });
      const done = () => { el.style.overflow = ""; };
      a.finished.then(done, done);
    }
    if (fadeIn.current) {
      fadeIn.current = false;
      [...el.children].forEach((k) => k.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 140, easing: "ease-out" }));
    }
  }, [shown]);

  const body = (m, withRefs) => (
    <DrawerBody
      {...props}
      mode={m}
      escapeRef={withRefs ? props.escapeRef : undefined}
      lastTopRef={withRefs ? lastTopRef : null}
    />
  );
  const top = shown === "band" ? bandFirst(laneOrder(props.open), props.ctx && props.ctx.infoFor, props.byKey)[0] : null;
  const topNow = top ? top.issue_key : null;
  useLayoutEffect(() => { lastTopRef.current = topNow; });
  const colour = colourLevel();

  return (
    <>
      <section ref={bandRef} className={bandClass(shown)} aria-label="AI Recommendations" data-testid="n3-band" data-mode={shown} data-colour={colour}>
        {body(shown, true)}
      </section>
      {measuring && (
        <section ref={measureRef} className={`${bandClass(measuring)} n3-morph-measure`} aria-hidden="true" data-colour={colour}>
          {body(measuring, false)}
        </section>
      )}
    </>
  );
}
